import type { PortableTextBlock } from '@portabletext/types';

import { buildStatsRequestKey, DEFAULT_VENUE } from './stats';
import type {
  ArticleBlock,
  CollectedReferences,
  SanityGameweekRange,
  SanityPlayerLinkMark,
  SanityPlayerReference,
  StatsVenue
} from './types';

export const DEFAULT_FIXTURE_COUNT = 5;
// Keep in step with MAX_TICKER_GAMEWEEKS in the Studio schema; the Studio
// warns but does not block, so the web clamps as well.
export const MAX_TICKER_GAMEWEEKS = 5;

export const clampTickerCount = (count: number | undefined): number =>
  Math.min(count ?? DEFAULT_FIXTURE_COUNT, MAX_TICKER_GAMEWEEKS);

type BlockCollector = (
  block: ArticleBlock,
  collected: CollectedReferences
) => void;

const addPlayer = (
  collected: CollectedReferences,
  player: SanityPlayerReference | undefined
): string | undefined => {
  if (player?.optaUuid) collected.optaUuids.add(player.optaUuid);
  return player?.optaUuid;
};

const addStatsRequest = (
  collected: CollectedReferences,
  venue: StatsVenue | undefined,
  range: SanityGameweekRange | undefined,
  optaUuids: Array<string | undefined>
) => {
  const resolvedVenue = venue ?? DEFAULT_VENUE;
  const key = buildStatsRequestKey(resolvedVenue, range);
  const request = collected.statsRequests.get(key) ?? {
    key,
    venue: resolvedVenue,
    minGameweek: range?.minGameweek,
    maxGameweek: range?.maxGameweek,
    optaUuids: new Set<string>()
  };
  for (const optaUuid of optaUuids) {
    if (optaUuid) request.optaUuids.add(optaUuid);
  }
  collected.statsRequests.set(key, request);
};

/**
 * One entry per custom `_type` an article body can carry. Adding a block means
 * adding its collector here and its renderer in the portable-text map; nothing
 * else needs to know about it.
 */
const COLLECTORS: Record<ArticleBlock['_type'], BlockCollector> = {
  playerFixturesBlock: (block, collected) => {
    if (block._type !== 'playerFixturesBlock') return;
    addPlayer(collected, block.player);
    collected.needsTeamFixtures = true;
  },
  playerProfileBlock: (block, collected) => {
    if (block._type !== 'playerProfileBlock') return;
    const optaUuid = addPlayer(collected, block.player);
    addStatsRequest(collected, block.venue, block.gameweekRange, [optaUuid]);
    collected.needsTeamFixtures = true;
  },
  headToHeadBlock: (block, collected) => {
    if (block._type !== 'headToHeadBlock') return;
    const one = addPlayer(collected, block.playerOne);
    const two = addPlayer(collected, block.playerTwo);
    addStatsRequest(collected, block.venue, block.gameweekRange, [one, two]);
    collected.needsTeamFixtures = true;
  },
  fixtureTickerBlock: (block, collected) => {
    if (block._type !== 'fixtureTickerBlock') return;
    collected.needsTeamFixtures = true;
    collected.fixtureRanges.push({
      startGameweek: block.startGameweek,
      count: clampTickerCount(block.gameweekCount)
    });
  },
  playerListBlock: (block, collected) => {
    if (block._type !== 'playerListBlock') return;
    for (const player of block.players ?? []) addPlayer(collected, player);
  },
  // Content-only blocks: nothing to fetch.
  statCalloutBlock: () => undefined,
  ctaBannerBlock: () => undefined,
  embedBlock: () => undefined,
  // Members-only data, so it is read in the browser, never at build time.
  plannedTeamBlock: () => undefined,
  predictedPointsBlock: () => undefined
};

export const ARTICLE_BLOCK_TYPES = Object.keys(COLLECTORS) as Array<
  ArticleBlock['_type']
>;

const PLAYER_LINK_MARK_TYPE = 'playerLink';

const isPlayerLinkMark = (markDef: unknown): markDef is SanityPlayerLinkMark =>
  typeof markDef === 'object' &&
  markDef !== null &&
  (markDef as SanityPlayerLinkMark)._type === PLAYER_LINK_MARK_TYPE &&
  typeof (markDef as SanityPlayerLinkMark).optaUuid === 'string' &&
  (markDef as SanityPlayerLinkMark).optaUuid.length > 0;

const referencedMarkKeys = (block: PortableTextBlock): Set<string> =>
  new Set(
    (block.children ?? []).flatMap(
      (child) => (child as { marks?: string[] }).marks ?? []
    )
  );

const collectPlayerLinks = (
  block: PortableTextBlock,
  collected: CollectedReferences
) => {
  // A definition no span references any more (the editor deleted the text)
  // would otherwise still cost API calls for a link that never renders.
  const referenced = referencedMarkKeys(block);
  for (const markDef of block.markDefs ?? []) {
    if (!isPlayerLinkMark(markDef) || !referenced.has(markDef._key)) continue;
    collected.optaUuids.add(markDef.optaUuid);
    collected.linkedOptaUuids.add(markDef.optaUuid);
  }
};

const isArticleBlock = (block: unknown): block is ArticleBlock =>
  typeof block === 'object' &&
  block !== null &&
  '_type' in block &&
  ARTICLE_BLOCK_TYPES.includes((block as ArticleBlock)._type);

export const collectArticleReferences = (
  body: PortableTextBlock[] | undefined
): CollectedReferences => {
  const collected: CollectedReferences = {
    optaUuids: new Set(),
    linkedOptaUuids: new Set(),
    needsTeamFixtures: false,
    statsRequests: new Map(),
    fixtureRanges: []
  };

  for (const block of body ?? []) {
    if (isArticleBlock(block)) COLLECTORS[block._type](block, collected);
    else if (block._type === 'block') collectPlayerLinks(block, collected);
  }

  return collected;
};

export const hasArticleBlocks = (collected: CollectedReferences): boolean =>
  collected.optaUuids.size > 0 ||
  collected.needsTeamFixtures ||
  collected.statsRequests.size > 0;
